import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, FileSpreadsheet, Inbox, Info, UploadCloud, X,
} from 'lucide-react';
import {
  downloadWcdpBatchReport,
  exportWcdpIas,
  WcdpAlreadyExported,
  WcdpExportError,
  WcdpExportResult,
} from '../../api/wcdpIasExport';
import { Supervisor, SupervisorsEmployee } from '../../interfaces/SupervisorInterface';
import {
  plural,
  sortByAltEmployeeID,
  wcdpChipKind,
  WcdpExportMap,
  wcdpInitialSelection,
  wcdpSelectableIDs,
  wcdpSelectedNeedingForce,
  wcdpShortDate,
  wcdpWithoutRejected,
} from './wcdpExportModel';

/* ─────────────────────────────────────────────────────────────────── props */

export interface WcdpIasExportModalProps {
  /** The board's Payroll Approved group — one supervisor's crew. Its employees ARE the list. */
  crew: Supervisor;
  payPeriodID: number;
  week: number | string;
  /** The pay period's IAS export record (board status fetch). Empty when it never loaded. */
  exported: WcdpExportMap;
  onClose: () => void;
  /** Called after any attempt that may have written a record, so the board refetches status. */
  onRecorded: () => void;
}

type Phase = 'select' | 'confirm' | 'exporting' | 'done' | 'fatal';

/** One row of the "Export again?" list — from the board record, or the API's 409 body. */
interface ReExportRow {
  employeeID: number;
  label: string;
  exportedAt: string;
  batchID: string;
  confirmed: boolean;
}

/* ────────────────────────────────────────────────────────────────── helpers */

// Duplicated from the board module scope (importing them back would be circular).
function initials(first: string, last: string): string {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}
function hashHue(s: string): number {
  let h = 7;
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) & 0x7fffffff;
  return h % 360;
}
const empLabel = (e: SupervisorsEmployee) => `${e.name.last}, ${e.name.first} #${e.altEmployeeID}`;

const REASON_TEXT: Record<string, string> = {
  notFound: 'not found',
  notPayrollApproved: 'not Payroll Approved',
  notInCrew: 'not on this crew',
  missingAltEmployeeID: 'no employee ID',
  timecardFailed: 'timecard failed',
  earningsCodeTooLong: 'earnings code too long',
  prdaAdjustmentExists: 'PRDA adjustment exists',
  timesheetChanged: 'timesheet changed',
};

/**
 * WCDP → IAS (Solomon TimeEntry_App) export for ONE supervisor's crew (TAS-682).
 *
 * A plain checklist of the crew as the board already has it (no refetch), sorted by employee
 * ID. Employees already exported this pay period start unchecked and "Select all" skips them;
 * ticking one routes through an "Export again" confirm and sends `force`. The server is
 * all-or-nothing: a 422 lists every rejected employee, they are unticked here, and the rest
 * can go in one click. Layout never moves: the body has a fixed height for every phase, and
 * notices live in a reserved, scrolling region under the list.
 */
const WcdpIasExportModal: React.FC<WcdpIasExportModalProps> = ({
  crew, payPeriodID, week, exported, onClose, onRecorded,
}) => {
  const employees = useMemo(() => sortByAltEmployeeID(crew.employees ?? []), [crew.employees]);
  const [sel, setSel] = useState<Set<number>>(() => wcdpInitialSelection(crew.employees ?? [], exported));
  const [phase, setPhase] = useState<Phase>('select');
  /** 422 / other refusals shown in the notice region of the select phase. */
  const [error, setError] = useState<WcdpExportError | null>(null);
  /** The force flag the last attempt used — "Export the other N" keeps it. */
  const [lastForce, setLastForce] = useState(false);
  const [confirmRows, setConfirmRows] = useState<ReExportRow[]>([]);
  const [result, setResult] = useState<WcdpExportResult | null>(null);
  const [fatal, setFatal] = useState<WcdpExportError | null>(null);
  const [reportBusy, setReportBusy] = useState<'regular' | 'dd' | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);

  const crewName = `${crew.name.last}, ${crew.name.first}`;
  const metaLine = [crewName, crew.altEmployeeID ? `Super #${crew.altEmployeeID}` : null, `Week ${week}`]
    .filter(Boolean).join(' · ');

  const selectable = useMemo(() => wcdpSelectableIDs(employees, exported), [employees, exported]);
  const selectableOn = selectable.reduce((n, id) => n + (sel.has(id) ? 1 : 0), 0);
  const allOn = selectable.length > 0 && selectableOn === selectable.length;
  const someOn = selectableOn > 0 && !allOn;
  const selectedIDs = useMemo(
    () => employees.filter((e) => sel.has(e.employeeID)).map((e) => e.employeeID),
    [employees, sel]);
  const needForce = useMemo(() => wcdpSelectedNeedingForce(sel, exported), [sel, exported]);

  const busy = phase === 'exporting';
  const closable = !busy && reportBusy === null;

  /* ── actions ───────────────────────────────────────────────────────────── */

  const toggle = (id: number) => {
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = (on: boolean) => {
    setSel((prev) => {
      const next = new Set(prev);
      selectable.forEach((id) => { if (on) next.add(id); else next.delete(id); });
      return next;
    });
  };

  const run = async (ids: number[], force: boolean) => {
    if (!ids.length) return;
    setPhase('exporting');
    setError(null);
    setLastForce(force);
    try {
      const res = await exportWcdpIas(crew.employeeID, payPeriodID, ids, force);
      setResult(res);
      setPhase('done');
      onRecorded();
    } catch (err) {
      const e = err instanceof WcdpExportError
        ? err
        : new WcdpExportError(err instanceof Error ? err.message : 'The batch export failed.');
      if (e.status === 409 && e.alreadyExported?.length) {
        // The board's record was stale (or never loaded): the server's list drives the confirm.
        const byID = new Map(employees.map((x) => [x.employeeID, x]));
        setConfirmRows(e.alreadyExported.map((a: WcdpAlreadyExported) => {
          const emp = byID.get(a.employeeID);
          return {
            employeeID: a.employeeID,
            label: a.name ? `${a.name}${a.altEmployeeID ? ` #${a.altEmployeeID}` : ''}` : emp ? empLabel(emp) : `#${a.altEmployeeID}`,
            exportedAt: a.exportedAt,
            batchID: a.batchID,
            confirmed: a.confirmed !== false,
          };
        }));
        setPhase('confirm');
        onRecorded();
        return;
      }
      if (e.unconfirmed || e.iasCommitted) {
        setFatal(e);
        setPhase('fatal');
        onRecorded();
        return;
      }
      if (e.status === 422 && e.rejections?.length) {
        setSel((prev) => wcdpWithoutRejected(prev, e.rejections ?? []));
      }
      setError(e);
      setPhase('select');
    }
  };

  const submit = () => {
    if (!selectedIDs.length) return;
    if (needForce.length) {
      const byID = new Map(employees.map((x) => [x.employeeID, x]));
      setConfirmRows(needForce.map((id) => {
        const x = exported.get(id)!;
        const emp = byID.get(id);
        return {
          employeeID: id,
          label: emp ? empLabel(emp) : `Employee ${id}`,
          exportedAt: x.exportedAt,
          batchID: x.batchID,
          confirmed: x.confirmed !== false,
        };
      }));
      setError(null);
      setPhase('confirm');
      return;
    }
    void run(selectedIDs, false);
  };

  const downloadReport = async (kind: 'regular' | 'dd', batchID: string) => {
    if (reportBusy) return;
    setReportBusy(kind);
    setReportError(null);
    try {
      await downloadWcdpBatchReport(batchID);
    } catch (err) {
      setReportError(err instanceof Error && err.message ? err.message : 'Could not generate the report.');
    } finally {
      setReportBusy(null);
    }
  };

  /* ── pieces ────────────────────────────────────────────────────────────── */

  const head = (icon: React.ReactNode, tone: string, title: string) => (
    <div className="modal-head">
      <span className={`mi${tone ? ` ${tone}` : ''}`}>{icon}</span>
      <div>
        <h3>{title}</h3>
        <div className="ms">{metaLine}</div>
      </div>
      {closable && <button className="x" onClick={onClose} type="button" aria-label="Close"><X /></button>}
    </div>
  );

  const batchCard = (batchID?: string, batchID_DD?: string) => (
    <div className="vx-sumcard">
      <div className="vx-sumrow">
        <span className="lb">Batch ID</span>
        <span className="vl mono">{batchID || '—'}</span>
      </div>
      <div className="vx-sumrow">
        <span className="lb">Batch ID (Direct Deposit)</span>
        <span className="vl mono">{batchID_DD || '—'}</span>
      </div>
    </div>
  );

  /** Regular / DD report buttons. Fixed width; the spinner replaces the icon, never the label. */
  const reportButtons = (batchID?: string, batchID_DD?: string, regularRows?: number, ddRows?: number) => (
    <>
      <div className="wx-reports">
        <button
          className="tr-btn wx-repbtn"
          onClick={() => { if (batchID) void downloadReport('regular', batchID); }}
          disabled={!batchID || regularRows === 0 || reportBusy !== null}
          type="button"
        >
          {reportBusy === 'regular' ? <span className="vx-spin sm" /> : <FileSpreadsheet />} Regular Report
        </button>
        <button
          className="tr-btn wx-repbtn"
          onClick={() => { if (batchID_DD) void downloadReport('dd', batchID_DD); }}
          disabled={!batchID_DD || ddRows === 0 || reportBusy !== null}
          type="button"
        >
          {reportBusy === 'dd' ? <span className="vx-spin sm" /> : <FileSpreadsheet />} Direct Deposit Report
        </button>
      </div>
      <div className="wx-reperr" role="status">{reportError ?? ''}</div>
    </>
  );

  /** The reserved notice region under the list — always the same height. */
  const notice = () => {
    if (error?.rejections?.length) {
      const rest = selectedIDs.length;
      return (
        <div className="vx-note bad">
          <AlertTriangle />
          <span>
            <b>{error.message}</b> Nothing was exported.
            <ul className="wx-list-plain">
              {error.rejections.map((r) => (
                <li key={`${r.employeeID}-${r.reason}`}>
                  <b>{r.name || `#${r.altEmployeeID}`}{r.name && r.altEmployeeID ? ` #${r.altEmployeeID}` : ''}</b>
                  {' '}&middot; {REASON_TEXT[r.reason] ?? r.reason}{r.message ? ` — ${r.message}` : ''}
                </li>
              ))}
            </ul>
            {rest > 0 && (
              <button className="tr-btn sm wx-other" onClick={() => { if (lastForce) void run(selectedIDs, true); else submit(); }} type="button">
                <UploadCloud /> Export the other {rest}
              </button>
            )}
          </span>
        </div>
      );
    }
    if (error) {
      return (
        <div className="vx-note bad">
          <AlertTriangle />
          <span><b>{error.message}</b>{error.status === 503 ? ' Nothing was written — try again shortly.' : ''}</span>
        </div>
      );
    }
    if (needForce.length) {
      return (
        <div className="vx-note warn">
          <AlertTriangle />
          <span>
            <b>{plural(needForce.length, 'selected employee')} {needForce.length === 1 ? 'was' : 'were'} already exported.</b>{' '}
            Exporting again writes their rows to Solomon a second time — you will be asked to confirm.
          </span>
        </div>
      );
    }
    return (
      <div className="vx-note info">
        <Info />
        <span>
          Everyone is selected except anyone already exported this week. The export is
          all-or-nothing: if any selected employee can&rsquo;t go, nothing is written.
        </span>
      </div>
    );
  };

  /* ── render ────────────────────────────────────────────────────────────── */

  return (
    <div className="trb-modal-bg" onClick={() => { if (closable) onClose(); }}>
      <div className="trb-modal vx-modal wide wx-modal" onClick={(e) => e.stopPropagation()}>

        {(phase === 'select' || phase === 'exporting') && (
          <>
            {head(<UploadCloud />, '', 'Batch Export')}
            <div className="modal-body tr-exp wx-body">
              {employees.length === 0 ? (
                <div className="vx-deadend">
                  <span className="tile"><Inbox /></span>
                  <h4>Nothing to export for this crew</h4>
                  <p>{crewName} has no Payroll Approved timesheets in Week {week}.</p>
                </div>
              ) : (
                <>
                  <label className="tr-exp-all wx-all">
                    <input
                      type="checkbox"
                      ref={(el) => { if (el) el.indeterminate = someOn; }}
                      checked={allOn}
                      onChange={(e) => toggleAll(e.target.checked)}
                      disabled={selectable.length === 0 || busy}
                    />
                    <span className="tr-exp-all-lab">Select all not exported</span>
                    <span className="tr-exp-count">
                      <b>{selectedIDs.length}</b> of <b>{employees.length}</b> selected
                    </span>
                  </label>

                  <div className="tr-exp-list wx-list">
                    {employees.map((e) => {
                      const hue = hashHue(`${e.name.first} ${e.name.last}`);
                      const on = sel.has(e.employeeID);
                      const x = exported.get(e.employeeID);
                      const kind = wcdpChipKind(x);
                      return (
                        <label key={e.employeeID} className={`tr-exp-row wx-row${on ? ' on' : ''}`}>
                          <input type="checkbox" checked={on} onChange={() => toggle(e.employeeID)} disabled={busy} />
                          <span
                            className="tr-exp-av"
                            style={{ background: `hsl(${hue} 60% 92%)`, color: `hsl(${hue} 55% 38%)` }}
                          >
                            {initials(e.name.first, e.name.last)}
                          </span>
                          <span className="tr-exp-who">
                            <span className="tr-exp-nm">{e.name.last}, {e.name.first}</span>
                            <span className="tr-exp-sub">#{e.altEmployeeID}</span>
                          </span>
                          {x && (
                            <span className={`vx-offtag tr-sagetag wx-tag ${kind === 'exported' ? 'ok' : 'warn'}`}>
                              {kind === 'unconfirmed' ? 'Unconfirmed'
                                : kind === 'changed' ? 'Changed'
                                : `Exported ${wcdpShortDate(x.exportedAt)}`}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>

                  <div className="wx-notice">{notice()}</div>
                </>
              )}
            </div>
            <div className="modal-foot tr-exp-foot">
              <span className="vx-grow" />
              <button className="tr-btn" onClick={onClose} disabled={busy} type="button">Cancel</button>
              <button
                className="tr-btn primary wx-go"
                onClick={submit}
                disabled={busy || selectedIDs.length === 0}
                type="button"
              >
                {busy ? <span className="vx-spin sm wx-spin-ink" /> : <UploadCloud />} Export selected
              </button>
            </div>
          </>
        )}

        {phase === 'confirm' && (
          <>
            {head(<AlertTriangle />, 'warn', 'Export again?')}
            <div className="modal-body vx-confirm wx-body wx-scroll">
              <p className="tr-exp-intro">
                {plural(confirmRows.length, 'employee')} {confirmRows.length === 1 ? 'was' : 'were'} already
                batch exported this week. Export {plural(selectedIDs.length, 'selected employee')} again
                as a NEW batch?
              </p>
              <div className="vx-note warn">
                <AlertTriangle />
                <span>
                  <b>The earlier rows stay in Solomon.</b> Unless the earlier batch is deleted in
                  Solomon, these employees will be paid twice.
                  <ul className="wx-list-plain">
                    {confirmRows.map((r) => (
                      <li key={r.employeeID}>
                        <b>{r.label}</b> &middot; exported {wcdpShortDate(r.exportedAt)} &middot; batch {r.batchID || '—'}
                        {!r.confirmed && <> &middot; <b>unconfirmed — check Solomon</b></>}
                      </li>
                    ))}
                  </ul>
                </span>
              </div>
            </div>
            <div className="modal-foot tr-exp-foot">
              <button className="tr-btn" onClick={() => setPhase('select')} type="button">Back</button>
              <span className="vx-grow" />
              <button className="tr-btn" onClick={onClose} type="button">Cancel</button>
              <button className="tr-btn danger wx-go" onClick={() => void run(selectedIDs, true)} type="button">
                <UploadCloud /> Export again
              </button>
            </div>
          </>
        )}

        {phase === 'done' && result && (
          <>
            {head(<CheckCircle2 />, 'ok', 'Batch exported')}
            <div className="modal-body vx-confirm wx-body wx-scroll">
              <p className="tr-exp-intro">
                {result.message?.trim()
                  || `${plural(result.rowCount, 'row')} for ${plural(result.employeeCount, 'employee')} exported.`}
              </p>
              {batchCard(result.batchID, result.batchID_DD)}
              <div className="vx-sumcard">
                <div className="vx-sumrow">
                  <span className="lb">Employees</span>
                  <span className="vl mono">{result.employeeCount}</span>
                </div>
                <div className="vx-sumrow">
                  <span className="lb">Rows (regular / direct deposit)</span>
                  <span className="vl mono">{result.regularRowCount} / {result.ddRowCount}</span>
                </div>
                <div className="vx-sumrow">
                  <span className="lb">PRDA records</span>
                  <span className="vl mono">{result.prdaRecordCount}</span>
                </div>
              </div>
              {reportButtons(result.batchID, result.batchID_DD, result.regularRowCount, result.ddRowCount)}
              {result.warning && (
                <div className="vx-note warn">
                  <AlertTriangle />
                  <span>{result.warning}</span>
                </div>
              )}
            </div>
            <div className="modal-foot tr-exp-foot">
              <span className="vx-grow" />
              <button className="tr-btn primary" onClick={onClose} disabled={!closable} type="button">Close</button>
            </div>
          </>
        )}

        {phase === 'fatal' && fatal && (
          <>
            {head(<AlertTriangle />, 'bad', fatal.iasCommitted ? 'Batch export not recorded' : 'Batch export unconfirmed')}
            <div className="modal-body vx-confirm wx-body wx-scroll">
              <div className="vx-note bad wx-fatal">
                <AlertTriangle />
                <span>
                  <b>Do NOT export again — tell Steven.</b>{' '}
                  {fatal.iasCommitted
                    ? 'The rows were written to Solomon, but the export could not be recorded here.'
                    : 'Solomon did not confirm the export. The rows MAY be in Solomon; the export is recorded as unconfirmed.'}
                  <div className="wx-fatalmsg">{fatal.message}</div>
                </span>
              </div>
              {batchCard(fatal.batchID, fatal.batchID_DD)}
              {reportButtons(fatal.batchID, fatal.batchID_DD)}
            </div>
            <div className="modal-foot tr-exp-foot">
              <span className="vx-grow" />
              <button className="tr-btn" onClick={onClose} disabled={!closable} type="button">Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WcdpIasExportModal;
